import { ActivityEntityName, DomainType, FeatureFlags, Job, SurfaceType } from '@jms/model';
import { type EntityManifestCard } from '@workbuddy/services/entities/entity.manifest';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { JobRelationName } from './job.relation.names';
import { resolveJobRecordRoute } from './job.routes';

// Job entity manifest: QuickView preview + Manage edit, board bulk actions, and the relation
// builders Job Manage mounts (scope hosts swap Job vs Stage at runtime).

const card: EntityManifestCard<Job> = {
  component: () => import('./presentation/card.job').then(module => module.CardJobComponent),
  resolution: 'snapshot',
  summarize: value => ({ title: value.jobNumber ?? 'Not set' }),
};

export const jobManifest = registerManifest<Job>({
  entity: ActivityEntityName.Job,
  slug: 'job',

  board: {
    // Job is field-shaped, so its selection shows in a narrow pinnable quickview rather than the
    // wide 50/50 document aside the financial entities use.
    panel: {
      kind: 'quickview',
    },
    bulkActionHandlers: {
      'activity.note.create': () => import('./bulk.actions/job.activity.note.bulk.action').then(m => m.handleJobActivityNoteBulkAction),
      'status.change': () => import('./bulk.actions/job.status.bulk.action').then(m => m.handleJobStatusBulkAction),
    },
  },

  surfaces: {
    [SurfaceType.QuickView]: () => import('./surface/job.quick.view.surface').then(m => m.jobQuickViewSurface),
    [SurfaceType.Manage]: () => import('./surface/manage/job.manage.surface').then(m => m.jobManageSurface),
  },

  operations: {
    // Preview stays capability-only: reading a job the board already listed grants nothing extra.
    preview: SurfaceType.QuickView,
    // Manage rights and record lifecycle are readonly policy inside Job Manage.
    edit: { surface: SurfaceType.Manage },
  },

  routes: {
    root: '/jobs/operations',
    record: resolveJobRecordRoute,
    fullPageFeatureFlag: FeatureFlags.JobManageSurface,
  },

  relations: {
    // Pure Job-owned builders. Job Manage mounts scope hosts via the surface runtime.
    [JobRelationName.Items]: () => import('./relations/job.items.relation').then(m => m.jobItemsBoardBuilder),
    [JobRelationName.Sor]: () => import('./relations/job.sor.relation').then(m => m.jobSorBoardBuilder),
    [JobRelationName.Tasks]: () => import('./relations/job.tasks.relation').then(m => m.jobTasksBoardBuilder),
    [JobRelationName.Invoices]: () => import('./relations/job.invoices.relation').then(m => m.jobInvoicesBoardBuilder),
    [JobRelationName.Bills]: () => import('./relations/job.bills.relation').then(m => m.jobBillsBoardBuilder),
    [JobRelationName.Quotes]: () => import('./relations/job.quotes.relation').then(m => m.jobQuotesBoardBuilder),
    [JobRelationName.SaleOrders]: () => import('./relations/job.sale.orders.relation').then(m => m.jobSaleOrdersBoardBuilder),
    [JobRelationName.PurchaseOrders]: () => import('./relations/job.purchase.orders.relation').then(m => m.jobPurchaseOrdersBoardBuilder),
    [ActivityEntityName.File]: () => import('./relations/job.files.relation').then(m => m.jobFilesBoardBuilder),
    [ActivityEntityName.Activity]: () => import('./relations/job.activities.relation').then(m => m.jobActivitiesBoardBuilder),
  },

  sectionRelations: {
    [ActivityEntityName.ContactLink]: {
      allowExisting: true,
      allowAdhoc: true,
      relationshipTags: true,
      parentScope: job => [job.customer?._id, job.site?._id].filter((id): id is string => !!id),
      globalSearch: false,
    },
    [ActivityEntityName.Flag]: true,
  },

  // A job's tasks carry every manual toggle the legacy task editor offered.
  shareRelations: {
    [ActivityEntityName.Task]: { customer: true, field: true, sensitive: true },
  },

  presentation: {
    card,
  },

  domains: {
    [DomainType.ClientPortal]: {
      presentation: {
        card,
      },
    },
    [DomainType.ContractorPortal]: {
      presentation: {
        card,
      },
    },
  },
});
